//@flow
import React, { Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { trackEvent } from 'dibs-tracking';
import getter from 'dibs-object-getter';
import classnames from 'classnames';
import { ACTIVE, PENDING, NEW, DELETED } from '../../constants';

import { Button } from 'dibs-elements/exports/Button';

import styles from './ProfileStatusModule.scss';

import type { ProfileStatusModule_tradeFirm as TradeFirmType } from './__generated__/ProfileStatusModule_tradeFirm.graphql';
import type { ProfileStatus } from '../../constants';

type ProfileStatusModuleProps = {
    tradeFirm: TradeFirmType,
    className?: string,
};

type BodyElementProps = {|
    urlLabel: string,
    pointsNeededToLevelUp: string,
    projectCount: number,
    tpnRole: ?string,
    profileStatus: ?ProfileStatus,
|};

function navigateToEditor({ profileStatus, urlLabel }: BodyElementProps): () => void {
    return () => {
        trackEvent({
            category: 'trade features',
            action: 'trade rewards',
            label: 'trade rewards module CTA clicked',
            additional: { profileStatus },
        });
        window.location.href = `/design-firms/${urlLabel}/edit/`;
    };
}

const Active = (props: BodyElementProps) => (
    <Fragment>
        <div className={styles.doubleIconWrapper}>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col1)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_profile.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.active.profile.header"
                        defaultMessage="Firm Profile Status"
                    />
                </div>
                <div className={styles.iconValue} data-tn="profile-status">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.active.profile.status"
                        defaultMessage="Published"
                    />
                </div>
                <div className={styles.iconLink}>
                    <a
                        href={`/design-firms/${props.urlLabel}/edit`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.active.profile.linkLabel"
                            defaultMessage="View / Edit Profile"
                        />
                    </a>
                </div>
            </div>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col2)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_projects.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.active.project.header"
                        defaultMessage="Published Projects"
                    />
                </div>
                <div className={styles.iconCountValue} data-tn="project-count">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.active.project.status"
                        defaultMessage="{count}"
                        values={{
                            count: props.projectCount,
                        }}
                    />
                </div>
                <div className={styles.iconLink}>
                    <a
                        href={`/design-firms/${props.urlLabel}/edit`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.active.project.linkLabel"
                            defaultMessage="View / Edit Projects"
                        />
                    </a>
                </div>
            </div>
        </div>
        <div className={styles.ctaContainer}>
            <div className={styles.cta}>
                <Button
                    dataTn="trade-profile-status-cta"
                    fullWidth
                    onClick={navigateToEditor(props)}
                    opensInNewWindow
                    size="medium"
                    type="primary"
                >
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.cta.active"
                        defaultMessage="Add New Projects"
                    />
                </Button>
            </div>
        </div>
    </Fragment>
);

const NoTpn = (props: BodyElementProps) => (
    <Fragment>
        <div className={`${styles.doubleIconWrapper} ${styles.noPadding}`}>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col1)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_profile.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.noTpn.profile.header"
                        defaultMessage="Design Profile"
                    />
                </div>
                <div className={styles.iconValue} data-tn="profile-status">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.noTpn.profile.status"
                        defaultMessage="Draft"
                    />
                </div>
            </div>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col2)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_projects.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.noTpn.project.header"
                        defaultMessage="Draft Projects"
                    />
                </div>
                <div className={styles.iconCountValue} data-tn="project-count">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.noTpn.project.status"
                        defaultMessage="{count}"
                        values={{
                            count: <FormattedNumber value={0} />,
                        }}
                    />
                </div>
            </div>
        </div>
        <div className={styles.label}>
            <FormattedMessage
                id="rewards.ProfileStatusModule.Body.noTpn."
                defaultMessage="Reach an audience of over 2 million registered users"
            />
        </div>
        <div className={styles.ctaContainer}>
            <Button
                className={styles.cta}
                size="medium"
                type="primary"
                onClick={navigateToEditor(props)}
                dataTn="trade-finish-profile"
                opensInNewWindow
            >
                <FormattedMessage
                    id="rewards.ProfileStatusModule.cta.noTpn"
                    defaultMessage="Finish Profile"
                />
            </Button>
        </div>
    </Fragment>
);

const Pending = (props: BodyElementProps) => (
    <Fragment>
        <div className={styles.doubleIconWrapper}>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col1)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_profile.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.pending.profile.header"
                        defaultMessage="Firm Profile Status"
                    />
                </div>
                <div className={styles.iconValue} data-tn="profile-status">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.pending.profile.status"
                        defaultMessage="Draft"
                    />
                </div>
            </div>
            <div className={classnames(styles.iconContainer, styles.gridRow1Col2)}>
                <div className={styles.icon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_projects.svg" />
                </div>
                <div className={styles.iconLabel}>
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.pending.project.header"
                        defaultMessage="Published Projects"
                    />
                </div>
                <div className={styles.iconCountValue} data-tn="project-count">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.Body.pending.project.status"
                        defaultMessage="{count}"
                        values={{
                            count: <FormattedNumber value={0} />,
                        }}
                    />
                </div>
            </div>
        </div>
        <div className={styles.ctaContainer}>
            <Button
                className={styles.cta}
                size="medium"
                type="primary"
                onClick={navigateToEditor(props)}
                dataTn="trade-edit-design-firms-cta"
                opensInNewWindow
            >
                <FormattedMessage
                    id="rewards.ProfileStatusModule.cta.pending"
                    defaultMessage="Complete Profile"
                />
            </Button>
        </div>
    </Fragment>
);

const DefaultBody = (props: BodyElementProps) => (
    <Fragment>
        <div className={styles.iconContainer}>
            <div className={styles.linkedIconWrapper}>
                <div className={styles.leftIcon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_profile.svg" />
                </div>
                <div className={styles.rightIcon}>
                    <img src="https://a.1stdibscdn.com/assets/trade/rewards/designer_projects.svg" />
                </div>
            </div>
            {props.tpnRole ? (
                <Fragment>
                    <div className={styles.iconLabel}>
                        <FormattedMessage
                            id="reward.ProfileStatusModule.Body.inProgressHeader"
                            defaultMessage="Interiors Profile"
                        />
                    </div>
                    <div className={styles.iconCountValue} data-tn="profile-status">
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.inProgressMessage"
                            defaultMessage="In Progress"
                        />
                    </div>
                    <div className={styles.linkedIconLabel}>
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.preparing"
                            defaultMessage="We are currently creating your profile and will contact you when it is complete."
                        />
                    </div>
                </Fragment>
            ) : (
                <Fragment>
                    <div className={styles.iconLabel}>
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.newHeader"
                            defaultMessage="Points needed to Publish Profile Onsite"
                        />
                    </div>
                    <div className={styles.iconCountValue}>
                        <FormattedNumber
                            value={props.pointsNeededToLevelUp}
                            maximumFractionDigits={0}
                        />
                    </div>
                    <div className={styles.linkedIconLabel}>
                        <FormattedMessage
                            id="rewards.ProfileStatusModule.Body.new"
                            defaultMessage="Get exposure to our high-net worth customers with your firm’s portfolio showcased on our site."
                        />
                    </div>
                </Fragment>
            )}
        </div>
    </Fragment>
);

const ProfileStatusModule = (props: ProfileStatusModuleProps) => {
    const profileStatus = getter(props.tradeFirm).value('profileStatus') || DELETED;

    const urlLabel =
        getter(props.tradeFirm)
            .at('publicProfile')
            .value('urlLabel') || '';

    const projectCount =
        getter(props.tradeFirm)
            .at('publicProfile')
            .value('designProjectsCount') || 0;

    const pointsNeededToLevelUp = getter(props.tradeFirm).value('pointsNeededToLevelUp') || '0';

    let BodyEl = null;
    const tpnRole = getter(props)
        .at('tradeFirm')
        .at('tpnRole')
        .value('code');

    const hasNoTpn = !tpnRole;

    switch (profileStatus) {
        case ACTIVE:
            BodyEl = Active;
            break;
        case PENDING:
            BodyEl = Pending;
            break;
        case NEW:
        case DELETED:
        default:
            BodyEl = hasNoTpn ? NoTpn : DefaultBody;
            break;
    }

    const header = hasNoTpn ? (
        <FormattedMessage
            id="rewards.ProfileStatusModule.noTpnHeader"
            defaultMessage="Firm Profile"
        />
    ) : (
        <FormattedMessage
            id="rewards.ProfileStatusModule.Header"
            defaultMessage="Your Firm's Profile"
        />
    );

    return (
        <div className={classnames(styles.wrapper, props.className)}>
            {hasNoTpn && (
                <div className={styles.banner} data-tn="earn-status-to-publish">
                    <FormattedMessage
                        id="rewards.ProfileStatusModule.banner"
                        defaultMessage="Reach Bronze Status to Publish Your Profile"
                    />
                </div>
            )}
            <div className={styles.header}>{header}</div>
            <div className={styles.bar} />
            <BodyEl
                urlLabel={urlLabel}
                pointsNeededToLevelUp={pointsNeededToLevelUp}
                projectCount={projectCount}
                tpnRole={tpnRole}
            />
        </div>
    );
};

export { ProfileStatusModule };

export default createFragmentContainer(
    ProfileStatusModule,
    graphql`
        fragment ProfileStatusModule_tradeFirm on TradeFirmType {
            serviceId
            profileStatus
            pointsNeededToLevelUp
            tpnRole {
                code
            }
            publicProfile {
                urlLabel
                designProjectsCount
            }
        }
    `
);
